import React from 'react';
import { useTranslation } from 'react-i18next';

import { stripHtml } from 'core/utils/utils';

import { getUserIdFromStorage } from '../../../services/serviceConfig';
import { formatShortDate, getInitials } from '../../../utils/formatUtils';
import SeverityBadge from '../SeverityIndicator/SeverityBadge';
import './_radar-card.scss';

import type { RadarAlert } from '../../../types/radar';

type RadarCardProps = {
  alert: RadarAlert;
  onClick?: (alert: RadarAlert) => void;
};

function RadarCard({ alert, onClick }: RadarCardProps): JSX.Element {
  const { t } = useTranslation(['chatbot']);

  const isPending = alert.status === 'PENDING';
  const statusModifier = alert.status === 'REVIEWED' || alert.status === 'RESOLVED' ? 'attended' : alert.status === 'DISMISSED' ? 'dismissed' : 'pending';

  const typeLabel = alert.type === 'inappropriate-use' ? t('chatbot:radar_type_inappropriate_use') : t('chatbot:radar_type_learning_block');

  const sanitizedLocations = alert.locations.map((loc) => stripHtml(loc)).filter(Boolean);

  const currentUserId = getUserIdFromStorage();
  const isReviewedByMe = alert.reviewedById && alert.reviewedById === currentUserId;
  const showReviewer = alert.reviewedById && (alert.status === 'REVIEWED' || alert.status === 'RESOLVED');
  const showDismisser = alert.reviewedById && alert.status === 'DISMISSED';
  const reviewerDisplayName = isReviewedByMe ? t('chatbot:radar_reviewed_by_you') : (alert.reviewedByName ?? '');
  const hasFooter = sanitizedLocations.length > 0 || showReviewer || showDismisser;

  function handleClick(): void {
    onClick?.(alert);
  }

  function handleKeyDown(e: React.KeyboardEvent): void {
    if (onClick && (e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      onClick(alert);
    }
  }

  const interactiveProps = onClick
    ? {
        onClick: handleClick,
        onKeyDown: handleKeyDown,
        role: 'button' as const,
        tabIndex: 0 as const,
      }
    : undefined;

  return (
    <div className={`radar-card radar-card--${statusModifier}${!alert.viewed ? ' radar-card--unread' : ''}`} {...interactiveProps}>
      <div className="radar-card__content">
        <div className="radar-card__header">
          <div className={`radar-card__avatar ${!alert.viewed && isPending ? 'radar-card__avatar--unread' : ''}`}>
            {alert.avatar ? (
              <img alt={`${alert.studentName} ${alert.studentLastName}`} className="radar-card__avatar-img" src={alert.avatar} />
            ) : (
              getInitials(alert.studentName, alert.studentLastName)
            )}
          </div>
          <div className="radar-card__header-info">
            <div className="radar-card__name-row">
              <span className="radar-card__student-name">
                {alert.studentName} {alert.studentLastName}
              </span>
              <span className="radar-card__tag">{typeLabel}</span>
            </div>
            <span className="radar-card__subtitle">{alert.groupName || formatShortDate(alert.createdAt)}</span>
          </div>
          {/* <button type="button" className="radar-card__menu-btn" aria-label={t('chatbot:radar_options')}>
            <Icon type="more-bullets" size="default" />
          </button> */}
        </div>

        <div className="radar-card__body">
          <div className="radar-card__body-left">
            <div className={`radar-card__bubble radar-card__bubble--${statusModifier}`}>
              <p className="radar-card__bubble-text">{alert.content}</p>
            </div>

            {hasFooter && (
              <div className="radar-card__footer">
                <div className="radar-card__footer-left">
                  {sanitizedLocations.length > 0 && (
                    <ul className="radar-card__locations">
                      {sanitizedLocations.map((loc) => (
                        <li key={loc} className="radar-card__location">
                          {loc}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="radar-card__footer-right">
                  {showReviewer && (
                    <span className="radar-card__reviewer">
                      <span className="radar-card__reviewer-label">{t('chatbot:radar_reviewed_by')}</span>{' '}
                      <span className="radar-card__reviewer-name">{reviewerDisplayName}</span>
                    </span>
                  )}
                  {showDismisser && (
                    <span className="radar-card__reviewer">
                      <span className="radar-card__reviewer-label">{t('chatbot:radar_dismissed_by')}</span>{' '}
                      <span className="radar-card__reviewer-name">{reviewerDisplayName}</span>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          <SeverityBadge severity={alert.severity} />
        </div>
      </div>
    </div>
  );
}

export default RadarCard;
